import React, { memo, useCallback, useState, useEffect } from 'react';
import { areEqual } from 'Utils/equalityChecks';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { setSelectedProjectId } from 'Containers/Projects/actions';

import { TimesheetsList, TimesheetsPagination } from 'Components/TimeTracking';
import { TimeTrackingCardTimesheet } from 'Containers/TimeTracking/types';

import { listTimeCardNotes } from 'Containers/TimeTracking/actions';

import { useTimeTrackingFunctions } from 'Context/TimeTracking';
import { useUser } from 'Context/User';

interface Props {
  getTimeCards: (e: any) => void;
  pageSize: number;
  onPageSizeChange: (size: number) => void;
  sortBy: string;
  onSortChange: (sort: string) => void;
}

const TimesheetsListContainer = ({ getTimeCards, pageSize, onPageSizeChange, sortBy, onSortChange }: Props) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const user = useUser();

  const {
    companyTimeCards,
    setSelectedTimeCardId,
    setIsEditTimeCardModalOpen,
    setIsDeleteTimeCardModalOpen,
    setIsTimeCardNotesModalOpen,
    setSelectedTimeCard,
  }: any = useTimeTrackingFunctions();

  const [pageCount, setPageCount] = useState(1);
  const [initialPage, setInitialPage] = useState(0);

  // sort by: -field === DESC, field === ASC
  // prefix with '-' for DESC
  const onClickSort = useCallback(
    (sort: string) => {
      if (sortBy.includes('-') && sortBy === sort) {
        onSortChange(sort.replace('-', ''));
      } else {
        onSortChange(sort);
      }
    },
    [sortBy, onSortChange]
  );

  // set meta data
  useEffect(() => {
    if (companyTimeCards?.data?.length > 0) {
      const { meta } = companyTimeCards;
      const { total } = meta;

      setPageCount(Math.ceil(total / pageSize));
    }
  }, [companyTimeCards, pageSize]);

  const onPageChange = useCallback(({ selected: page }: any) => {
    setInitialPage(page);
    getTimeCards(page);
  }, []);

  const onClickProject = useCallback((e: any) => {
    const {
      dataset: { id },
    } = e.currentTarget;

    dispatch(setSelectedProjectId(id));
    history.push(`/projects/${id}/rocketscan`);
  }, []);

  const onClickEditIcon = useCallback(
    (e: any) => {
      if (companyTimeCards?.data?.length > 0) {
        const {
          dataset: { id },
        } = e.currentTarget;

        setSelectedTimeCardId(id);
        const card = companyTimeCards.data.find((timecard: TimeTrackingCardTimesheet) => timecard.id === id);
        setSelectedTimeCard(card);
        setIsEditTimeCardModalOpen(true);
      }
    },
    [companyTimeCards]
  );

  const onClickNotesIcon = useCallback(
    (e: any) => {
      if (companyTimeCards?.data?.length > 0) {
        const {
          dataset: { id },
        } = e.currentTarget;

        setSelectedTimeCardId(id);
        dispatch(listTimeCardNotes(id));
        setIsTimeCardNotesModalOpen(true);
      }
    },
    [companyTimeCards]
  );

  const onClickDeleteIcon = useCallback(
    (e: any) => {
      if (companyTimeCards?.data?.length > 0) {
        const {
          dataset: { id },
        } = e.currentTarget;

        setSelectedTimeCardId(id);
        const card = companyTimeCards.data.find((timecard: TimeTrackingCardTimesheet) => timecard.id === id);
        setSelectedTimeCard(card);
        setIsDeleteTimeCardModalOpen(true);
      }
    },
    [companyTimeCards]
  );

  const onRowsPerPageChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      onPageSizeChange(Number(e.target.value));
    },
    [onPageSizeChange]
  );

  const rowsPerPageSelect = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#5b476b' }}>
      <span>Rows per page:</span>
      <select
        value={pageSize}
        onChange={onRowsPerPageChange}
        style={{
          padding: '4px 8px',
          borderRadius: '4px',
          border: '1px solid #e8e7ed',
          color: '#5b476b',
          fontSize: '14px',
        }}
      >
        <option value={50}>50</option>
        <option value={100}>100</option>
        <option value={200}>200</option>
        <option value={300}>300</option>
        <option value={500}>500</option>
      </select>
    </div>
  );

  const totalCount = companyTimeCards?.meta?.total || 0;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0' }}>
        {rowsPerPageSelect}
        {totalCount > 0 && (
          <span style={{ fontSize: '14px', color: '#5b476b', fontWeight: 500 }}>
            {totalCount} {totalCount === 1 ? 'record' : 'records'}
          </span>
        )}
      </div>
      <TimesheetsList
        sortBy={sortBy}
        timeCards={companyTimeCards}
        isCompanyAdmin={user.isCompanyAdmin}
        currency="USD"
        onClickSort={onClickSort}
        onClickProject={onClickProject}
        onClickEdit={onClickEditIcon}
        onClickNotes={onClickNotesIcon}
        onClickDelete={onClickDeleteIcon}
      />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0' }}>
        {rowsPerPageSelect}
        {companyTimeCards?.meta?.total > pageSize && (
          <TimesheetsPagination initialPage={initialPage} pageCount={pageCount} onPageChange={onPageChange} />
        )}
      </div>
    </div>
  );
};

const TimesheetsListContainerMemo = memo(TimesheetsListContainer, areEqual);

export { TimesheetsListContainerMemo as TimesheetsList };
